import { Fragment, useEffect, useState, useMemo, useRef } from 'react';
import {
  Theme,
  TextLink,
  TextLinkStyle,
  useBreakpoint,
  Breakpoints,
  useTranslation,
  useSession,
} from '@nextgen-web-framework/all';
import {
  Filter,
  filterInitialState,
  BucketField,
  debounce,
  NO_RESULT_FILTERS,
} from '@commerce-search-webcomponents/sdk';
import { PLP_TYPE } from '../types/enums';
import { FacetsProps, PRICE_SORT_KEYS, PriceRangeTypes } from './types/propTypes';
import FacetSection from './FacetSection';
import FacetsDrawer from './FacetsDrawer';
import styles from './Facets.module.scss';

const priceInputMaxLength = 10;
const debounceDelay = 1000;

const Facets = ({
  categoryAndSolutionDetails,
  priceRangeInterval,
  facets,
  filter,
  plpType,
  noResultFilterName,
  setShowFacetsDrawer,
  updateFilter,
  resetFilter,
}: FacetsProps) => {
  const [isApplyBtnEnabled, setIsApplyBtnEnabled] = useState(false);
  const [isResetBtnEnabled, setIsResetBtnEnabled] = useState(false);
  const [minPrice, setMinPrice] = useState<string>('');
  const [maxPrice, setMaxPrice] = useState<string>('');
  const [minPriceError, setMinPriceError] = useState<boolean>(false);
  const [maxPriceError, setMaxPriceError] = useState<boolean>(false);
  const [priceRangeError, setPriceRangeError] = useState<boolean>(false);
  const viewport = useBreakpoint() as Breakpoints;
  const t = useTranslation();
  const [stateCopy, setStateCopy] = useState<Filter>(filter);
  const desktopView = useMemo(() => [Breakpoints.LG, Breakpoints.XL].includes(viewport), [viewport]);
  const priceErrorRef = useRef<Record<string, unknown>>({});
  const { isABO, isMember, isEmployee } = useSession();
  priceErrorRef.current = { minPriceError, maxPriceError }; // to use latest value inside callback
  const priceSortKey = isEmployee()
    ? PRICE_SORT_KEYS.EMPLOYEE_PRICE
    : isABO() || isMember()
      ? PRICE_SORT_KEYS.ABO_PRICE
      : PRICE_SORT_KEYS.CUST_PRICE;
  const sorting = {
    field: BucketField.SORT,
    values: [
      { name: '', label: t('search.facets.sortByRelevance') },
      { name: `${priceSortKey}:asc`, label: t('search.facets.sortByPriceAsc') },
      { name: `${priceSortKey}:desc`, label: t('search.facets.sortByPriceDesc') },
    ],
  };
  // config to decide which filter is hidden for which PLP type
  const facetInvisibilityConfig = {
    [PLP_TYPE.CATEGORY]: BucketField.SOLUTION,
    [PLP_TYPE.SOLUTION]: BucketField.CATEGORY,
    [PLP_TYPE.BRAND]: BucketField.BRAND,
  };

  useEffect(() => {
    setStateCopy(filter);
    setIsResetBtnEnabled(!compareState(filter, filterInitialState));
  }, [filter]);

  useEffect(() => {
    setPriceRangeError(noResultFilterName === NO_RESULT_FILTERS.PRICE);
  }, [noResultFilterName]);

  useEffect(() => {
    const [min = '', max = ''] = stateCopy.price?.split(' - ') || [];
    setMinPrice(min);
    setMaxPrice(max);
    const isFilterApplied = !compareState(stateCopy, filter);
    if (desktopView) {
      if (isFilterApplied) {
        updateFilter(stateCopy);
      }
    } else {
      setIsApplyBtnEnabled(isFilterApplied);
    }
  }, [stateCopy]);

  useEffect(() => {
    validatePriceInput(setMinPriceError);
  }, [minPrice]);

  useEffect(() => {
    validatePriceInput(setMaxPriceError);
  }, [maxPrice]);

  useEffect(() => {
    const [minPrev = ''] = filter.price?.split(' - ') || [];
    if (minPrev === minPrice) {
      setPriceRangeError(noResultFilterName === NO_RESULT_FILTERS.PRICE);
    }
    if (minPrice === '' && maxPrice === '') {
      setPriceRangeError(false);
    }
  }, [minPrice, maxPrice, filter, noResultFilterName]);

  const validatePriceInput = (setErrorFn: (hasError: boolean) => void): void => {
    if (minPrice !== '' && maxPrice !== '' && Number(minPrice) >= Number(maxPrice)) {
      setErrorFn(true);
    } else {
      resetPriceError();
      debounceUpdatePriceInput();
    }
  };

  const resetPriceError = () => {
    setMinPriceError(false);
    setMaxPriceError(false);
  };

  const resetFilterFn = () => {
    resetFilter();
    resetPriceError();
  };

  const compareState = (state1: Filter, state2: Filter) => {
    let isEqual = true;
    for (const key of Object.keys(state1) as Array<keyof Filter>) {
      if (Array.isArray(state1[key])) {
        if (
          state2[key]?.length !== state1[key]?.length ||
          !(state1[key] as string[]).every((value) => state2[key]?.includes(value))
        ) {
          isEqual = false;
          break;
        }
      } else if (state1[key] !== state2[key]) {
        isEqual = false;
        break;
      }
    }
    return isEqual;
  };

  const checkboxOnChange = (section: keyof Filter, name: string) => {
    const array = ((stateCopy[section] as string[]) || [])?.slice();
    const index = array.indexOf(name);
    if (index > -1) {
      array.splice(index, 1);
    } else {
      array.push(name);
    }
    setStateCopy({ ...stateCopy, [section]: array });
  };

  const priceInputOnChange = (e: React.ChangeEvent<HTMLInputElement>, callback: (priceValue: string) => void) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (value === '' || (value.length <= priceInputMaxLength && !/[^0-9]/.test(value))) {
      callback(value);
      setPriceRangeError(false);
    }
  };

  const priceInputOnClear = (e: React.MouseEvent, type: string) => {
    setPriceRangeError(false);
    if (type === PriceRangeTypes.MIN_PRICE) {
      setMinPrice('');
      setMinPriceError(false);
    } else if (type === PriceRangeTypes.MAX_PRICE) {
      setMaxPrice('');
      setMaxPriceError(false);
    }
    debounceUpdatePriceInput();
  };

  const debounceUpdatePriceInput = () => {
    debounce(debounceDelay, () => {
      if (!priceErrorRef.current.minPriceError && !priceErrorRef.current.maxPriceError) {
        const value = minPrice !== '' || maxPrice !== '' ? `${minPrice} - ${maxPrice}` : '';
        setStateCopy({ ...stateCopy, price: value });
      }
    });
  };

  const facetsContent = (
    <div className={styles.facets}>
      {desktopView && (
        <div className={styles['facets-header']}>
          <h4 className={styles.title}>{t('search.common.sortAndFilter')}</h4>
          <TextLink
            data-testid="facets-clear-all-testid"
            text={t('search.common.clearAll')}
            textLinkStyle={TextLinkStyle.UNDERLINE}
            theme={Theme.PURPLE}
            onClick={(e) => {
              e.preventDefault();
              if (isResetBtnEnabled) {
                resetFilterFn();
              }
            }}
          />
        </div>
      )}

      <div className={styles['facet-group']}>
        {[sorting, ...facets].map(
          (bucket) =>
            (!plpType || facetInvisibilityConfig[plpType] !== bucket.field) &&
            Number(bucket.values?.length) > 0 && (
              <Fragment key={bucket.field}>
                <FacetSection
                  bucket={bucket}
                  filterData={stateCopy}
                  categoryAndSolutionDetails={categoryAndSolutionDetails}
                  priceRangeInterval={priceRangeInterval}
                  isPLP={Boolean(plpType)}
                  priceRangeError={priceRangeError}
                  hideItemCount={noResultFilterName === NO_RESULT_FILTERS.PRICE}
                  updateFilterData={setStateCopy}
                  checkboxOnChange={checkboxOnChange}
                  minPrice={minPrice}
                  maxPrice={maxPrice}
                  minPriceError={minPriceError}
                  maxPriceError={maxPriceError}
                  onMinPriceChange={(e) => priceInputOnChange(e, setMinPrice)}
                  onMaxPriceChange={(e) => priceInputOnChange(e, setMaxPrice)}
                  priceInputOnClear={priceInputOnClear}
                />
              </Fragment>
            ),
        )}
      </div>
    </div>
  );

  const handleApplyClick = () => {
    if (isApplyBtnEnabled) {
      updateFilter(stateCopy);
      setShowFacetsDrawer(false);
    }
  };

  const handleResetClick = () => {
    if (isResetBtnEnabled) {
      resetFilterFn();
      setShowFacetsDrawer(false);
    }
  };

  return desktopView ? (
    facetsContent
  ) : (
    <FacetsDrawer
      isApplyBtnDisabled={!isApplyBtnEnabled || minPriceError || maxPriceError}
      isResetBtnDisabled={!isResetBtnEnabled}
      onApply={handleApplyClick}
      onReset={handleResetClick}
      onClose={() => setShowFacetsDrawer(false)}
    >
      {facetsContent}
    </FacetsDrawer>
  );
};

export default Facets;
